/**
 * Personnel — employment records, and the identity store they point at.
 *
 * **Every nullable field is optional, not `| null`.** The API sets
 * `JsonIgnoreCondition.WhenWritingNull`, so a null property is not sent at all — it arrives
 * as `undefined`. Typing one as `string | null` is a lie the compiler cannot catch, and
 * `=== null` against it is always false. Check falsiness, never identity.
 *
 * The shape to keep in mind while reading these: **no row in this file carries a name.**
 * A list row carries a `personId`, and the page resolves the ids it is showing in one batch
 * call. That is `reference/INTEGRATION_REFERENCE.md` §4.2, and it is also why there is no
 * `lastName` on `PersonnelListItem` for a column to bind to — the names live in `rsys_pii`,
 * under a login this application's main connection does not hold.
 */

/** Recorded to match a birth certificate; not a question about identity. */
export type Sex = 1 | 2;

export const SEX_LABELS: Record<Sex, string> = {
  1: 'Female',
  2: 'Male',
};

/**
 * Citizenship, per the CSC Personal Data Sheet.
 *
 * An enum rather than a catalog: the PDS offers exactly these two answers, so a third would
 * be a change to the form rather than to a list an administrator maintains.
 */
export type Citizenship = 1 | 2;

export const CITIZENSHIP_LABELS: Record<Citizenship, string> = {
  1: 'Filipino',
  2: 'Dual citizenship',
};

/** How a dual citizen came by the second citizenship. Only meaningful alongside `2` above. */
export type DualCitizenshipKind = 1 | 2;

export const DUAL_CITIZENSHIP_KIND_LABELS: Record<DualCitizenshipKind, string> = {
  1: 'By birth',
  2: 'By naturalization',
};

export type CivilStatus = 1 | 2 | 3 | 4 | 5 | 6;

export const CIVIL_STATUS_LABELS: Record<CivilStatus, string> = {
  1: 'Single',
  2: 'Married',
  3: 'Widowed',
  4: 'Separated',
  5: 'Annulled',
  6: 'Divorced',
};

/** How someone is employed — not what post they hold. */
export type EmploymentType = 1 | 2 | 3 | 4 | 5 | 6;

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  1: 'Permanent',
  2: 'Temporary',
  3: 'Casual',
  4: 'Contractual',
  5: 'Coterminous',
  6: 'Job order',
};

export interface PersonnelListItem {
  id: string;

  /**
   * The opaque reference the page resolves to a display name. Never rendered raw.
   *
   * **Null when the record is above the reader's access level**, and that absence IS the mask:
   * the row is still sent and still counted, and the one thing that could turn it into a name
   * is withheld. Blanking the cell client-side would leave the value in the payload.
   */
  personId?: string | null;

  employeeNumber: string;
  sex: Sex;
  employmentType?: EmploymentType | null;

  /** The substantive post, or null while the record is unassigned. */
  itemNumber?: string | null;
  positionTitle?: string | null;
  orgUnitName?: string | null;

  /**
   * The account this employee signs in as, or null where none is linked.
   *
   * An id and nothing more — it is what lets a form answer "which of these is the reader",
   * which the Issue slip uses to default its <i>Issued by</i>.
   */
  userId?: string | null;

  /** The rung this file sits on, or null while it is unclassified. */
  accessLevelId?: string | null;

  /**
   * The rung's name — sent even for a restricted row, deliberately.
   *
   * A reader who cannot open the file is still told WHY, which is the difference between a
   * control and a bug they report. It discloses the classification, not the person.
   */
  accessLevelName?: string | null;
  accessLevelRank?: number | null;

  /**
   * True when this record is above the reader's clearance: no `personId`, and it cannot be
   * opened.
   *
   * Resolved for the CALLER by the server, like `awaitingMe` on an approval row. It is not
   * derivable here — the SPA does not know the reader's own rung, and giving it one to compare
   * against would be a second place the rule lives.
   */
  isRestricted?: boolean;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** One line of an employee's service record — the post held, and the appointment behind it. */
export interface PersonnelServiceRecord {
  id: string;
  plantillaItemId: string;
  itemNumber: string;
  positionTitle: string;
  orgUnitName: string;
  effectiveFrom: string;
  effectiveTo?: string | null;

  /** The day the appointment instrument is dated. NOT the effectivity. */
  appointmentDate?: string | null;

  /** The day the employee assumed the post. Never before the appointment date. */
  assumptionDate?: string | null;

  appointmentNatureId?: string | null;

  /**
   * What the movement is called — resolved by the server on every read, never stored.
   *
   * A LIVE view: correcting the vocabulary corrects every line filed under it, which is the
   * catalog's own argument for being a table.
   */
  appointmentNatureName?: string | null;

  isPrimary: boolean;
  remarks?: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface PersonnelEmergencyContact {
  id: string;

  /** A contact's name is subject PII too, so it is resolved the same way an employee's is. */
  personId: string;

  relationship: string;
  contactNumber: string;
  province?: string | null;
  cityMunicipality?: string | null;
  barangay?: string | null;
  streetAddress?: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

export interface PersonnelDetail {
  id: string;
  personId: string;
  employeeNumber: string;
  sex: Sex;
  civilStatus?: CivilStatus | null;

  /** Null on a record that predates the question — never defaulted to Filipino. */
  citizenship?: Citizenship | null;

  /** Both are refused unless the citizenship is dual — the server checks, not just the form. */
  dualCitizenshipKind?: DualCitizenshipKind | null;
  dualCitizenshipCountryId?: string | null;
  dualCitizenshipCountryName?: string | null;
  employmentType?: EmploymentType | null;
  contactNumber?: string | null;
  email?: string | null;
  userId?: string | null;

  /** An operator identity, not a subject — §4.1's standing exception. */
  userAlias?: string | null;

  /**
   * The rung this file sits on, or null while it is unclassified.
   *
   * A reader holding this record has already passed the clearance check — a record above their
   * level never reaches the page, because the server refuses the address. These three are
   * informational: the profile states what the file is classified as, which is what tells a
   * reader why their colleague cannot see it.
   */
  accessLevelId?: string | null;
  accessLevelName?: string | null;
  accessLevelRank?: number | null;

  serviceRecords: PersonnelServiceRecord[];
  educations: PersonnelEducation[];
  eligibilities: PersonnelEligibility[];
  workExperiences: PersonnelWorkExperience[];
  voluntaryWorks: PersonnelVoluntaryWork[];

  /** Learning and development, most recent first — CS Form 212, section VII. */
  trainings: PersonnelTraining[];

  /** Special skills and hobbies, BEST first — CS Form 212, section VIII, item 31. */
  specialSkills: PersonnelSpecialSkill[];

  /**
   * Non-academic distinctions and recognitions — CS Form 212, section VIII, item 32.
   *
   * Most recently awarded first, and rows nobody dated last — an unrecorded date is not a recent
   * one. The panel opens on the same sort.
   */
  recognitions: PersonnelRecognition[];

  /**
   * Character references, in the order they were recorded.
   *
   * Each row carries a `personId` and NO NAME — resolve a page of them through
   * `usePersonNames`, which is one batch call to the identity service.
   */
  references: PersonnelReference[];

  /**
   * SALN filings, most recent year first.
   *
   * Not a section of CS Form 212 — a SALN is its own statutory filing under RA 6713 that a
   * 201 file holds, which is why the panel appends after the form's own run.
   */
  salns: PersonnelSaln[];

  /** Disciplinary and administrative records, most recent incident first. */
  disciplinaryRecords: PersonnelDisciplinaryRecord[];
  familyMembers: PersonnelFamilyMember[];

  /**
   * The survey — CS Form 212, items 34 to 40. One row per question ANSWERED.
   *
   * A question the employee has not been asked yet has no row here, which is what lets the
   * panel report how many are outstanding.
   */
  surveyAnswers: PersonnelSurveyAnswer[];
  emergencyContacts: PersonnelEmergencyContact[];

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  dateHired: string | null;
  bloodTypeId: string | null;
  bloodTypeName: string | null;
  nationalityId: string | null;
  nationalityName: string | null;
  religionId: string | null;
  religionName: string | null;
  hmoProviderId: string | null;
  hmoProviderName: string | null;
  hmoCardNumber: string | null;
  identifications: PersonnelIdentificationValue[];
  isMilitary: boolean;
  militaryBranch: MilitaryBranch | null;
  militaryServiceStatus: MilitaryServiceStatus | null;
  militaryRank: string | null;
  militaryServiceNumber: string | null;
  militaryUnit: string | null;
  militaryDateEnteredService: string | null;
  militaryDateSeparated: string | null;
  isPwd: boolean;
  pwdIdNumber: string | null;
  permanentAddressSameAsPresent: boolean;
  addresses: PersonnelAddress[];
}

/**
 * What a `personId` resolves to.
 *
 * `status` distinguishes three absences that look alike and are not: a record the reader
 * deleted, one whose identity was erased and cannot come back, and an id the store has never
 * heard of. Rendering all three as a blank would turn two real facts into a rendering fault.
 */
export interface PersonSummary {
  personId: string;
  displayName: string;
  status: 'active' | 'deleted' | 'erased' | 'restricted' | 'unknown';
}

/** The full identity. `personnel.reveal` only, one record at a time, audited on every call. */
export interface PersonIdentity {
  personId: string;
  /** A TITLE — Mr., Dr., Atty. Optional; mirrored in salutations.ts. */
  salutation?: string | null;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  suffix?: string | null;
  dateOfBirth?: string | null;
}

export interface CreatePersonnelRequest {
  /** A TITLE — Mr., Dr., Atty. Optional; mirrored in salutations.ts. */
  salutation?: string | null;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  suffix?: string | null;
  dateOfBirth: string;
  sex: Sex;
  civilStatus?: CivilStatus | null;

  /** Null on a record that predates the question — never defaulted to Filipino. */
  citizenship?: Citizenship | null;

  /** Both are refused unless the citizenship is dual — the server checks, not just the form. */
  dualCitizenshipKind?: DualCitizenshipKind | null;
  dualCitizenshipCountryId?: string | null;
  employmentType?: EmploymentType | null;
  contactNumber?: string | null;
  email?: string | null;
  userId?: string | null;

  /**
   * The rung to classify the new file at, or null to leave it unclassified.
   *
   * **On CREATE only, and never on the update.** The gap between creating a record that should
   * be restricted and classifying it afterwards is a window in which everybody can read it, so
   * the wizard can close it — but a PUT rewrites every column it carries, and a reader holding
   * `personnel.update` and not `personnel.set-access-level` would then send back whatever their
   * form was given and have the whole request refused. Reclassifying is its own endpoint.
   *
   * Supplying one without `personnel.set-access-level` refuses the WHOLE request, server-side.
   */
  accessLevelId?: string | null;

  /**
   * The POSITION to appoint into. Optional: hiring and placement are not always the same
   * moment.
   *
   * **The forms send this, never an item id** — see {@link CreateServiceRecordRequest}.
   */
  plantillaPositionId?: string | null;

  /** An exact slot, for a caller that has one. The forms do not. */
  plantillaItemId?: string | null;

  /** Required when either post field is set. */
  effectiveFrom?: string | null;

  /**
   * The three service-record facts the Employment step collects alongside the post.
   *
   * They travel on the CREATE rather than a second call because this step writes the
   * employee's FIRST line of service; there is no edit for one, so a fact not collected here
   * is one the opening line of the 201 file never carries. Ignored when no post is named.
   */
  appointmentDate?: string | null;
  assumptionDate?: string | null;
  appointmentNatureId?: string | null;

  dateHired: string | null;
  bloodTypeId: string | null;
  nationalityId: string | null;
  religionId: string | null;
  hmoProviderId: string | null;
  hmoCardNumber: string | null;
  identifications: PersonnelIdentificationInput[];
  isMilitary: boolean;
  militaryBranch: MilitaryBranch | null;
  militaryServiceStatus: MilitaryServiceStatus | null;
  militaryRank: string | null;
  militaryServiceNumber: string | null;
  militaryUnit: string | null;
  militaryDateEnteredService: string | null;
  militaryDateSeparated: string | null;
  isPwd: boolean;
  pwdIdNumber: string | null;
  permanentAddressSameAsPresent: boolean;
  addresses: PersonnelAddressInput[];
  account: PersonnelAccountInput | null;
}

/**
 * No employee number and no service record — both are somebody else's operation.
 *
 * **The identity block is OPTIONAL, and that is what makes a dedicated per-section edit
 * possible.** `firstName`, `lastName` and `dateOfBirth` live in `rsys_pii`; send them to
 * change them, omit them and the identity row is not touched. A modal correcting an address
 * sends none of the six, so it needs `personnel.update` alone — before this it would have
 * had to send the person's name and date of birth, and therefore hold `personnel.reveal`.
 *
 * All-or-nothing: sending ANY of the six commits the caller to sending the name and the date
 * of birth, or the server rejects it rather than blanking what was left out. So a payload
 * built for a non-identity section must carry none of these keys at all — not even
 * `middleName: null`, which the server still counts as "the caller is changing the identity".
 */
export type UpdatePersonnelRequest = Omit<
  CreatePersonnelRequest,
  | 'plantillaPositionId'
  | 'plantillaItemId'
  | 'effectiveFrom'
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
> & {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
};

export interface CreateServiceRecordRequest {
  /**
   * The POSITION, not the slot — exactly one of the two, and the forms send this one.
   *
   * Only the server knows which slots are open at the moment of writing. A client picking
   * the lowest-numbered one out of a list it fetched when the modal opened hands two readers
   * appointing at once the same slot, and the loser is told a specific item is filled when
   * they never chose an item and the position still has seats.
   */
  plantillaPositionId?: string | null;

  /** An exact slot, for a caller that has one. The forms do not. */
  plantillaItemId?: string | null;
  effectiveFrom: string;

  /** All three optional: a line can be filed before the paper is in hand. */
  appointmentDate?: string | null;
  assumptionDate?: string | null;
  appointmentNatureId?: string | null;

  isPrimary: boolean;
  remarks?: string | null;
}

export interface EndServiceRecordRequest {
  effectiveTo: string;
  remarks?: string | null;
}

/**
 * Which service an employee served in. Fixed by law, so an enum rather than a catalog —
 * the same test that separates Sex from Nationality.
 */
export type MilitaryBranch = 1 | 2 | 3 | 4 | 5;

export const MILITARY_BRANCH_LABELS: Record<MilitaryBranch, string> = {
  1: 'Army',
  2: 'Navy',
  3: 'Air Force',
  4: 'National Police',
  5: 'Coast Guard',
};

export type MilitaryServiceStatus = 1 | 2 | 3 | 4;

export const MILITARY_SERVICE_STATUS_LABELS: Record<MilitaryServiceStatus, string> = {
  1: 'Active',
  2: 'Reserve',
  3: 'Retired',
  4: 'Separated',
};

/** Present is where they live now; permanent is where their records point. */
/**
 * What has been typed into the family form before.
 *
 * **Not a catalog** — nothing maintains these and nothing validates against them. A value
 * absent from the list is still accepted; the field commits whatever is typed. The trade for
 * not curating them is that the list grows with what people enter, typos included.
 */
export interface PersonnelFamilySuggestions {
  occupations: string[];
  employers: string[];
}

/*
  FamilyRelation and FAMILY_RELATION_LABELS lived here and are gone.

  The relation is a CATALOG now — `features/human-resources/family-relations` — so there is no
  union to narrow to and no label map to keep in step with the server's enum. A row carries
  `relationId` and the server resolves `relationName` on read, which is what the panel renders.

  Do not reintroduce a local label map. It would be a second copy of a vocabulary an
  administrator maintains, and it would silently stop covering a relation they add.
*/

/**
 * One member of an employee's family.
 *
 * **No name on this type, deliberately.** Every relative is a subject in the identity store,
 * so the row carries a `personId` and the panel resolves the ids on screen through
 * `usePersonNames` — one batch call for the page. A `name` field here would be the copy the
 * whole split exists to prevent.
 */
export interface PersonnelFamilyMember {
  id: string;

  /** The opaque reference the panel resolves to a display name. Never rendered raw. */
  personId: string;

  /** The catalog row this member is recorded at. */
  relationId: string;

  /** A LIVE view of the relation's name: correcting the vocabulary corrects every record. */
  relationName: string;

  /**
   * Where the relation sits in the form's order, so the panel can sort without a second call.
   *
   * Sorting on `relationName` would put "Child" above "Father", which is not how section II
   * reads — the catalog's own order is the form's order.
   */
  relationSortOrder: number;

  /** Asked only where the relation says so — `collectsOccupation`. Empty otherwise. */
  occupation?: string | null;
  employerName?: string | null;
  businessAddress?: string | null;
  telephoneNumber?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * What the modal sends.
 *
 * **The name fields are here and nowhere else.** They travel through to the identity store;
 * what comes back and stays in `rsys_` is the `personId` alone.
 */
export interface PersonnelFamilyMemberInput {
  /** The catalog row. A relation that has been deleted is refused on a fresh write. */
  relationId: string;
  /** A TITLE — Mr., Dr., Atty. Optional; mirrored in salutations.ts. */
  salutation?: string | null;
  firstName: string;
  middleName?: string | null;
  lastName: string;

  /** The form's "name extension" — Jr., Sr., III. */
  suffix?: string | null;

  /** Required where the chosen relation says so — `requiresDateOfBirth`. */
  dateOfBirth?: string | null;

  occupation?: string | null;
  employerName?: string | null;
  businessAddress?: string | null;
  telephoneNumber?: string | null;
}

/**
 * One civil service eligibility an employee has earned — CS Form 212, section IV.
 *
 * **Not the eligibility CATALOG** (`features/human-resources/eligibilities`), which is the
 * vocabulary an administrator maintains and a position names as a qualification standard.
 * This is one person holding one of those, with the rating, the date and the place.
 */
export interface PersonnelEligibility {
  id: string;
  eligibilityId: string;

  /** Resolved from the catalog on every read, never copied onto the row. */
  eligibilityName: string;

  /** The catalog's answer, not this row's — it decides whether the licence fields apply. */
  entailsLicense: boolean;

  /** Absent where the eligibility was conferred rather than examined for. */
  rating?: number | null;

  /** Calendar dates, not instants. */
  dateOfExamination: string;
  placeOfExamination?: string | null;

  licenseNumber?: string | null;
  licenseValidUntil?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * One position an employee has held — CS Form 212, section V.
 *
 * **Every field is a plain stored value.** The position title is not a `Position` id, the
 * agency is not an `OrgUnit` id, and the salary grade is not a `SalaryGrade` id — this row
 * records what an employee stated about an employer, and most employers are not this one. It
 * is the opposite call from `PersonnelEligibility`, which points at its catalog deliberately;
 * see the entity's own doc for why one module answers that question both ways.
 *
 * **Not `PersonnelServiceRecord`**, which is the plantilla item this organization has them
 * occupying now. That is a post in our staffing pattern; this is a line on a CV.
 */
export interface PersonnelWorkExperience {
  id: string;

  /** Calendar dates, not instants. */
  periodFrom: string;

  /** Null while the position is still held — the form's "PRESENT". */
  periodTo?: string | null;

  positionTitle: string;

  /** Department, agency, office or company — the form's four words for one column. */
  agencyName: string;

  monthlySalary?: number | null;

  /** Text, as the employer expressed it: "15", "24", "Band B". Never a salary-grade id. */
  salaryGrade?: string | null;

  stepIncrement?: string | null;

  appointmentStatus: EmploymentType;

  /** The form's Y/N: was this position in public or government employment? */
  isGovernmentService: boolean;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. */
export interface PersonnelWorkExperienceInput {
  periodFrom: string;
  periodTo?: string | null;
  positionTitle: string;
  agencyName: string;
  monthlySalary?: number | null;
  salaryGrade?: string | null;
  stepIncrement?: string | null;
  appointmentStatus: EmploymentType;

  /**
   * Required, and deliberately not defaulted.
   *
   * The server takes this as a nullable and refuses a request that omits it, because `false`
   * is not a neutral value here — it is the claim that the position was private employment,
   * and length of government service is computed from these rows.
   */
  isGovernmentService: boolean;
}

/**
 * One spell of voluntary work or involvement in an organization — CS Form 212, section VI.
 *
 * **Not `PersonnelWorkExperience`, and the difference is not seniority.** Section V is
 * employment: an appointment, a salary, a grade, a status of appointment, a yes/no about
 * government service. A volunteer is neither paid nor appointed, so none of those apply and
 * this row carries none of them — what it carries instead is the one quantity the form asks a
 * volunteer for, hours.
 *
 * Every field is a plain stored value, like section V's: the organization somebody volunteered
 * at is not this one, so there is nothing to resolve it against.
 */
export interface PersonnelVoluntaryWork {
  id: string;

  /** Calendar dates, not instants. */
  periodFrom: string;

  /** Null while the involvement continues — the form's "PRESENT". */
  periodTo?: string | null;

  /** Written in full, as the form asks. */
  organizationName: string;

  /** One line of free text — never an `AddressPlace` path. */
  organizationAddress: string;

  /** Absent where nobody recorded them. Zero is a real answer and a different one. */
  numberOfHours?: number | null;

  /** The post held, or the kind of work rendered — the form asks for either. */
  positionOrNature: string;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. */
export interface PersonnelVoluntaryWorkInput {
  periodFrom: string;
  periodTo?: string | null;
  organizationName: string;
  organizationAddress: string;

  /**
   * Optional, and left null rather than defaulted to zero.
   *
   * The opposite of `PersonnelWorkExperienceInput.isGovernmentService`, which the server
   * refuses when omitted: a missing boolean binds to `false`, which asserts something, while a
   * missing number stays null, which is exactly the fact "nobody recorded the hours".
   */
  numberOfHours?: number | null;

  positionOrNature: string;
}

/*
  LearningDevelopmentType, LearningDevelopmentCategory and DevelopmentInterventionKind WERE
  enums here, and are catalogs now — see features/human-resources/learning-development.

  The change is noted rather than made silently because the reasoning that put them here still
  reads as sound: CS Form 212 really does print a fixed set of answers. What it missed is that a
  201 file outlives the form — an employer files academic study, a professional review course, a
  mentoring assignment and a webinar on section VII, and behind an enum each of those was a
  migration, a DTO change and a form edit.

  The label maps went with them. A record now carries the vocabulary row's ID and its NAME,
  resolved from the catalog on every read, so nothing on this side maps a number to a word.

  CompetencyLevel STAYS an enum: it is an ordered ladder the server compares, and a rung added
  between Basic and Intermediate would change what the comparison means rather than add a row.
*/

/**
 * One ladder, and THREE records read it.
 *
 * `competencyLevelBefore` and `targetCompetencyLevel` are the level held and the level targeted
 * against the L&D framework; `PersonnelSpecialSkill.proficiencyLevel` is how capable somebody is
 * at a skill or hobby they listed themselves. Sharing the vocabulary is deliberate — a second
 * ladder with three of four members spelled identically is the "two words for one state"
 * failure — and it is the call `PersonnelWorkExperience.appointmentStatus` already takes on
 * `EmploymentType`.
 *
 * `0` — Not rated — is a real answer for the level BEFORE and refused everywhere else: a target
 * of "not rated" is not a target, and a skill's proficiency is CLAIMED rather than assessed, so
 * "never assessed" is not an answer somebody listing a hobby can honestly give. See
 * `SKILL_PROFICIENCY_LEVELS`, which is the list the skill form offers.
 *
 * The values are ordered, which is what lets the server catch a target at or below the level
 * already held — and what lets a proficiency column sort as a ranking rather than alphabetically.
 */
export type CompetencyLevel = 0 | 1 | 2 | 3 | 4;

export const COMPETENCY_LEVEL_LABELS: Record<CompetencyLevel, string> = {
  0: 'Not rated',
  1: 'Basic',
  2: 'Intermediate',
  3: 'Advanced',
  4: 'Expert',
};

/** One competency as the L&D form's picker and the record's chips need it. */
export interface CompetencyOption {
  id: string;
  name: string;
  category?: string | null;
}

/**
 * One learning-and-development record — CS Form 212, section VII.
 *
 * Captured from the hospital system's Trainings module — title, type, sponsor, inclusive dates,
 * hours — and widened with the competency-development half this product's own L&D form asks
 * for: what it was FOR, which competencies it addresses, and the journey from the level held to
 * the level targeted.
 *
 * **The competencies are a LIVE VIEW and the sponsor is a SNAPSHOT.** Correcting a competency's
 * wording corrects every record naming it; an institution that renames itself does not rewrite
 * the certificates it already issued. One row, both answers.
 */
export interface PersonnelTraining {
  id: string;

  /** The record's own permanent number, `LND-2026-00125`. Server-assigned, never sent. */
  recordNumber: string;

  title: string;

  /*
    Four vocabularies, each as an ID and a NAME. The pair is not duplication: the name is
    resolved from the catalog on every read rather than copied onto the row, so correcting a
    vocabulary's wording corrects every record naming it. The id is what the edit form
    re-selects with; the name is what the panel prints.
  */
  typeId: string;
  typeName: string;
  categoryId: string;
  categoryName: string;

  /** How it was delivered. Absent where nobody recorded it. */
  interventionId?: string | null;
  interventionName?: string | null;

  /** Who conducted or sponsored it. Absent where nobody recorded it. */
  sponsorId?: string | null;
  sponsorName?: string | null;

  /**
   * The competency-development block, withheld from the form on 15 Sep 2026.
   *
   * Absent on every record written since. Null is "nobody was asked", which is a different fact
   * from `NotRated`'s "somebody looked and found no assessment".
   */
  purpose?: string | null;
  competencyLevelBefore?: CompetencyLevel | null;
  targetCompetencyLevel?: CompetencyLevel | null;

  /** Calendar dates, not instants. Both absent where nobody recorded them. */
  dateFrom?: string | null;
  dateTo?: string | null;

  /** Absent where nobody counted them. Zero is a real answer and a different one. */
  numberOfHours?: number | null;

  /** The competencies addressed, in the framework's own order. */
  competencies: CompetencyOption[];

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. */
export interface PersonnelTrainingInput {
  title: string;

  /** A live row of the L&D type catalog. Required. */
  typeId: string;

  /** A live row of the L&D category catalog. Required. */
  categoryId: string;

  /** A live row of the development-intervention catalog, or null. */
  interventionId?: string | null;

  /** A live row of the sponsor catalog, or null. */
  sponsorId?: string | null;

  /**
   * The competency-development block. Optional, and the form no longer sends any of it.
   *
   * Kept on the contract so restoring the block is a form change rather than an API change. `0`
   * is still refused as a target — a target of "not rated" is not a target.
   */
  purpose?: string | null;
  competencyLevelBefore?: CompetencyLevel | null;
  targetCompetencyLevel?: CompetencyLevel | null;

  dateFrom?: string | null;
  dateTo?: string | null;
  numberOfHours?: number | null;

  /**
   * The competencies addressed. Part of the withheld competency-development block, so the form
   * sends none and omitting it records none.
   *
   * **No `recordNumber` anywhere on this type.** It is assigned by the server and permanent, so
   * the request carries no field for it at all — a request that CAN express one is a request
   * that will eventually set one by accident.
   */
  competencyIds?: string[];
}

/**
 * The proficiency ladder a special skill is offered, in order, BEST LAST.
 *
 * **`CompetencyLevel` reused rather than a second ladder coined beside it.** A proficiency at a
 * hobby and a proficiency at a competency are the same fact about the same person, and an enum
 * with three of four members spelled identically to an existing one is the "two words for one
 * state" failure. The call `PersonnelWorkExperience.appointmentStatus` already takes on
 * `EmploymentType`: reuse the vocabulary, refuse the value that does not apply.
 *
 * **`0` — Not rated — is the value that does not apply, so it is not in this list and the server
 * refuses it.** This level is CLAIMED rather than assessed: somebody listing a hobby knows
 * whether they are a beginner at it, and somebody who does not leaves the row unwritten.
 *
 * Ordered rather than alphabetical, and the picker is told not to re-sort it — a ladder whose
 * rungs read Advanced, Basic, Expert, Intermediate is a ladder nobody can read.
 */
export const SKILL_PROFICIENCY_LEVELS: readonly CompetencyLevel[] = [1, 2, 3, 4] as const;

/**
 * One special skill or hobby — CS Form 212, section VIII, item 31.
 *
 * **The category is a LIVE VIEW.** The name is resolved from the catalog on every read rather
 * than copied onto the row, so correcting the vocabulary corrects every record filed under it —
 * which is why the row carries both the id the form re-selects with and the name the panel
 * prints.
 *
 * **The skill itself is free text and deliberately not a catalog.** The tail is unbounded —
 * origami, conversational Hokkien, forklift operation — so a catalog would be a table nobody
 * could finish seeding. See `PersonnelSpecialSkill` on the server.
 */
export interface PersonnelSpecialSkill {
  id: string;

  /** "Public Speaking", "Photography". Free text, and the row's identity. */
  name: string;

  categoryId: string;

  /** Resolved through the join on every read — never a copy. */
  categoryName: string;

  /** Basic, Intermediate, Advanced or Expert. Never `0`. */
  proficiencyLevel: CompetencyLevel;

  /** Absent where nobody recorded them. Zero is a real answer and a different one. */
  yearsOfExperience?: number | null;

  remarks?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. */
export interface PersonnelSpecialSkillInput {
  name: string;

  /** A live row of the special-skill category catalog. Required. */
  categoryId: string;

  /** Required, and `0` is refused — this level is claimed rather than assessed. */
  proficiencyLevel: CompetencyLevel;

  /**
   * Optional, and left null rather than defaulted to zero.
   *
   * The same call `PersonnelVoluntaryWorkInput.numberOfHours` makes: a missing number stays
   * null, which is exactly the fact "nobody recorded it", where 0 would be the claim that they
   * took it up this year.
   */
  yearsOfExperience?: number | null;

  remarks?: string | null;
}

/**
 * One character reference — a person who can vouch for the employee.
 *
 * **Captured from the hospital system's own Reference module**, and this is the capture where
 * the shape had to change. That model carries `firstName`, `middleName` and `lastName` as
 * columns in its own main database; three of the four protected fields, on a row about somebody
 * who does not even work here.
 *
 * **So there is no name on this type at all.** A referee is a subject in their own right, so the
 * row carries a `personId` and the names live in `rsys_pii` — the same shape
 * `PersonnelFamilyMember` and the emergency contacts take. The panel resolves the ids on screen
 * through one batch call; a name on this interface would be the copy the split exists to prevent.
 *
 * **The consequence the panel has to live with:** the section cannot be sorted or searched by
 * name, and says so in its placeholder. Sorting would mean resolving the whole list before
 * paging it, and searching would mean copying names into `rsys_`.
 */
export interface PersonnelReference {
  id: string;

  /** The referee's opaque subject reference. Never rendered raw — resolve it. */
  personId: string;

  occupation?: string | null;
  company?: string | null;
  address?: string | null;
  contactNumber?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * What the modal sends.
 *
 * **The REQUEST carries names; the row and the DTO do not.** A caller supplying a name they
 * already know is not a reveal — it travels through the identity service and only the id it
 * hands back stays in `rsys_`.
 *
 * There is deliberately **no date of birth**: the form asks a referee for a name, an address and
 * a number, and a birth date is the most sensitive of the protected fields.
 */
export interface PersonnelReferenceInput {
  /** A TITLE — Mr., Dr., Atty. Optional; mirrored in salutations.ts. */
  salutation?: string | null;
  firstName: string;
  middleName?: string | null;
  lastName: string;

  /** "Jr.", "III". Part of the name, so it goes to the identity store with it. */
  suffix?: string | null;

  occupation?: string | null;
  company?: string | null;
  address?: string | null;
  contactNumber?: string | null;
}

/*
  ── Disciplinary and administrative records ───────────────────────────────────────────

  Captured from the hospital system on 18 Sep 2026. A sanction imposed, or a matter raised.

  ORDINARY LOCAL DATA, and worth saying because it feels otherwise: the record is about the
  employee whose file it is and names no other person. If a complainant or an investigating
  officer is ever collected, that person is a subject in their own right and carries a
  `personId`, exactly as a character reference does.
*/

/**
 * What the record IS. The ordinals mirror the C# enum, which serializes as a number.
 *
 * The first six are the CSC's ladder of sanctions; the rest are matters raised.
 */
export const DISCIPLINARY_ACTION_TYPE = {
  VerbalWarning: 0,
  WrittenWarning: 1,
  Reprimand: 2,
  Suspension: 3,
  Demotion: 4,
  Dismissal: 5,
  Memorandum: 6,
  NoticeToExplain: 7,
  ShowCauseOrder: 8,
  AdministrativeCase: 9,
  Complaint: 10,
  Other: 11,
} as const;

export type DisciplinaryActionType =
  (typeof DISCIPLINARY_ACTION_TYPE)[keyof typeof DISCIPLINARY_ACTION_TYPE];

export const DISCIPLINARY_ACTION_TYPE_LABELS: Record<DisciplinaryActionType, string> = {
  [DISCIPLINARY_ACTION_TYPE.VerbalWarning]: 'Verbal warning',
  [DISCIPLINARY_ACTION_TYPE.WrittenWarning]: 'Written warning',
  [DISCIPLINARY_ACTION_TYPE.Reprimand]: 'Reprimand',
  [DISCIPLINARY_ACTION_TYPE.Suspension]: 'Suspension',
  [DISCIPLINARY_ACTION_TYPE.Demotion]: 'Demotion',
  [DISCIPLINARY_ACTION_TYPE.Dismissal]: 'Dismissal',
  [DISCIPLINARY_ACTION_TYPE.Memorandum]: 'Memorandum',
  [DISCIPLINARY_ACTION_TYPE.NoticeToExplain]: 'Notice to explain',
  [DISCIPLINARY_ACTION_TYPE.ShowCauseOrder]: 'Show-cause order',
  [DISCIPLINARY_ACTION_TYPE.AdministrativeCase]: 'Administrative case',
  [DISCIPLINARY_ACTION_TYPE.Complaint]: 'Complaint',
  [DISCIPLINARY_ACTION_TYPE.Other]: 'Other',
};

/** A sanction against the employee, or a matter raised. */
export const DISCIPLINARY_CATEGORY = { Disciplinary: 0, Administrative: 1 } as const;

export type DisciplinaryCategory =
  (typeof DISCIPLINARY_CATEGORY)[keyof typeof DISCIPLINARY_CATEGORY];

export const DISCIPLINARY_CATEGORY_LABELS: Record<DisciplinaryCategory, string> = {
  [DISCIPLINARY_CATEGORY.Disciplinary]: 'Disciplinary',
  [DISCIPLINARY_CATEGORY.Administrative]: 'Administrative',
};

/** Where the matter has got to. NOT the soft-delete flag — see the entity. */
export const DISCIPLINARY_STATUS = {
  Open: 0,
  UnderInvestigation: 1,
  Resolved: 2,
  Dismissed: 3,
  Appealed: 4,
  Closed: 5,
} as const;

export type DisciplinaryStatus = (typeof DISCIPLINARY_STATUS)[keyof typeof DISCIPLINARY_STATUS];

export const DISCIPLINARY_STATUS_LABELS: Record<DisciplinaryStatus, string> = {
  [DISCIPLINARY_STATUS.Open]: 'Open',
  [DISCIPLINARY_STATUS.UnderInvestigation]: 'Under investigation',
  [DISCIPLINARY_STATUS.Resolved]: 'Resolved',
  [DISCIPLINARY_STATUS.Dismissed]: 'Dismissed',
  [DISCIPLINARY_STATUS.Appealed]: 'Appealed',
  [DISCIPLINARY_STATUS.Closed]: 'Closed',
};

/**
 * Whether the matter has ENDED — which is what decides the resolution date.
 *
 * **Appealed is not concluded.** An appeal is the case still running, so a resolution date on
 * it would say the matter finished on a day the employee was still contesting it. The server
 * holds the same line in `DisciplinaryCategories.IsConcluded`; the two must agree, or the form
 * asks for a date the save refuses.
 */
export function isDisciplinaryConcluded(status: DisciplinaryStatus): boolean {
  return (
    status === DISCIPLINARY_STATUS.Resolved ||
    status === DISCIPLINARY_STATUS.Dismissed ||
    status === DISCIPLINARY_STATUS.Closed
  );
}

/** One disciplinary or administrative record. */
export interface PersonnelDisciplinaryRecord {
  id: string;

  type: DisciplinaryActionType;

  /** DERIVED from the type server-side — never stored, and never sent back. */
  category: DisciplinaryCategory;

  caseNumber?: string | null;
  subject: string;
  description?: string | null;

  /** Calendar dates — render them with `formatDateOnly`, never `formatDate`. */
  incidentDate?: string | null;
  dateFiled?: string | null;

  actionTaken?: string | null;
  status: DisciplinaryStatus;
  resolutionDate?: string | null;
  remarks?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * What the modal sends.
 *
 * **No category.** It is read off the type server-side, so sending one would be supplying an
 * answer the server is about to overrule.
 */
export interface PersonnelDisciplinaryRecordInput {
  type: DisciplinaryActionType;
  caseNumber?: string | null;
  subject: string;
  description?: string | null;
  incidentDate?: string | null;
  dateFiled?: string | null;
  actionTaken?: string | null;
  status: DisciplinaryStatus;
  resolutionDate?: string | null;
  remarks?: string | null;
}

/*
  ── SALN ──────────────────────────────────────────────────────────────────────────────

  A Statement of Assets, Liabilities and Net Worth — the annual disclosure under RA 6713,
  captured from the hospital system on 17 Sep 2026.

  ORDINARY LOCAL DATA. A declared asset is sensitive and it is NOT the identity store's
  business: CLAUDE.md names a SALN filing as ordinary data in `rsys_`, and the government-ID
  move on the same day did not widen that. Nothing here carries a `personId`.
*/

/** One real-property line. */
export interface SalnRealProperty {
  id: string;
  description: string;
  exactLocation?: string | null;
  assessedValue?: number | null;
  fairMarketValue?: number | null;
  acquisitionYear?: number | null;
  acquisitionMode?: string | null;

  /** What was paid — the figure that counts toward total assets. */
  acquisitionCost?: number | null;
}

/** One personal-property line. */
export interface SalnPersonalProperty {
  id: string;
  description: string;
  yearAcquired?: number | null;
  acquisitionCost?: number | null;
}

/** One liability line. */
export interface SalnLiability {
  id: string;
  nature: string;
  creditorName?: string | null;

  /** What is still owed — the figure that counts toward total liabilities. */
  outstandingBalance?: number | null;
}

/**
 * One business interest or financial connection — the profile's **Connections** folder.
 *
 * **It counts toward no total**, which is the form's shape rather than an omission: an interest
 * is a connection to be disclosed, not an amount. Its folder shows a COUNT where the other
 * three show money.
 */
export interface SalnBusinessInterest {
  id: string;
  entityName: string;
  businessAddress?: string | null;
  natureOfInterest?: string | null;

  /** A calendar date — render it with `formatDateOnly`, never `formatDate`. */
  acquisitionDate?: string | null;
}

/**
 * One SALN filing.
 *
 * **The three totals are DERIVED SERVER-SIDE from the schedules below**, as get-only properties
 * over these very lists — never stored, and never recomputed at a call site. A second
 * arithmetic in the panel or the modal is exactly how a folder tab and the preview table under
 * it come to disagree about what a filing adds up to, so read them from here.
 */
export interface PersonnelSaln {
  id: string;

  /** The calendar year the declaration is as of. A year, not a date. */
  asOfYear: number;

  isJointFiling: boolean;
  remarks?: string | null;

  realProperties: SalnRealProperty[];
  personalProperties: SalnPersonalProperty[];
  liabilities: SalnLiability[];
  businessInterests: SalnBusinessInterest[];

  /** Real plus personal acquisition costs. Derived — do not recompute. */
  totalAssets: number;

  /** The sum of the outstanding balances. Derived — do not recompute. */
  totalLiabilities: number;

  /** Assets less liabilities. Derived, and it may legitimately be negative. */
  netWorth: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/*
  What the modal sends.

  NO ID AND NO SORT ORDER ON A LINE. A correction replaces the schedule wholesale — the server
  flags the old lines and writes these — so a client-supplied id would name a row it does not
  own, and the position comes from the ARRAY ORDER, which the server numbers.

  NO TOTALS EITHER: they are the sum of the lines, and a client-sent total is a second copy of
  one fact.
*/

export interface SalnRealPropertyInput {
  description: string;
  exactLocation?: string | null;
  assessedValue?: number | null;
  fairMarketValue?: number | null;
  acquisitionYear?: number | null;
  acquisitionMode?: string | null;
  acquisitionCost?: number | null;
}

export interface SalnPersonalPropertyInput {
  description: string;
  yearAcquired?: number | null;
  acquisitionCost?: number | null;
}

export interface SalnLiabilityInput {
  nature: string;
  creditorName?: string | null;
  outstandingBalance?: number | null;
}

export interface SalnBusinessInterestInput {
  entityName: string;
  businessAddress?: string | null;
  natureOfInterest?: string | null;
  acquisitionDate?: string | null;
}

export interface PersonnelSalnInput {
  asOfYear: number;
  isJointFiling: boolean;
  remarks?: string | null;
  realProperties: SalnRealPropertyInput[];
  personalProperties: SalnPersonalPropertyInput[];
  liabilities: SalnLiabilityInput[];
  businessInterests: SalnBusinessInterestInput[];
}

/**
 * One non-academic distinction or recognition — CS Form 212, section VIII, item 32.
 *
 * **Captured from the hospital system's own Recognition module**, kept to its four columns: the
 * award, who conferred it, when, and what it was for.
 *
 * **Nothing here is a resolved join**, unlike the special skill above it. An award points at no
 * catalog, because the body that conferred it is almost never this organization — the rule "a
 * record of what someone STATED has no foreign keys at all", reaching a third section.
 *
 * **Two rows may legitimately be identical.** Employee of the Year in 2019 and again in 2023 are
 * two awards, so the table carries no unique index and a second is a new row rather than a
 * correction of the first. That is the opposite of a skill, where a second row of one name is one
 * fact recorded twice.
 */
export interface PersonnelRecognition {
  id: string;

  /** "Employee of the Year". Free text, and the row's identity. */
  title: string;

  /** The body that conferred it. Absent where nobody recorded it. */
  awardedBy?: string | null;

  /**
   * A CALENDAR date — render it with `formatDateOnly`, never `formatDate`.
   *
   * Absent where nobody wrote it down, which is NOT the "PRESENT" a missing end date means on
   * work experience and voluntary work: an award is a moment rather than a period.
   */
  dateAwarded?: string | null;

  /** The citation — what it was given for. */
  description?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. No sort order — the section is ordered by the date it collects. */
export interface PersonnelRecognitionInput {
  title: string;
  awardedBy?: string | null;

  /** Optional, and refused in the future: a recognition is something received. */
  dateAwarded?: string | null;

  description?: string | null;
}

/**
 * One employee's answer to one survey question — CS Form 212, items 34 to 40.
 *
 * **Not the survey QUESTIONNAIRE** (`features/human-resources/survey`), which is the catalog an
 * administrator maintains under Personnel → Settings. This is one person having answered one of
 * its questions.
 *
 * The question's number, text and `collects*` flags ride on the row and none of them is a copy:
 * every one is resolved from the catalog on each read, so correcting a question's wording
 * corrects this. The flags travel because the panel and its form decide from them which
 * follow-ups apply, and they have only the answers in hand.
 */
export interface PersonnelSurveyAnswer {
  id: string;
  surveyQuestionId: string;

  /** Resolved from the catalog on every read, never copied onto the row. */
  questionNumber: string;
  questionText: string;

  /**
   * Where the question sits in the questionnaire.
   *
   * The panel sorts on this rather than on the number, whose TEXT sort files "40a" between
   * "34b" and "35a" the moment an employer numbers a question past nine.
   */
  questionSortOrder: number;

  /** The catalog's answers, not this row's — they decide which follow-ups apply. */
  collectsDetails: boolean;
  collectsDate: boolean;
  collectsCaseStatus: boolean;
  collectsIdNumber: boolean;
  collectsCountry: boolean;

  /** Shown under the answer form when this question is being corrected. */
  guidance?: string | null;

  /** The form's Yes or No. */
  answer: boolean;

  details?: string | null;

  /** A calendar date, not an instant. */
  dateFiled?: string | null;
  caseStatus?: string | null;
  idNumber?: string | null;

  countryId?: string | null;

  /** Resolved from the country catalog on every read. */
  countryName?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. */
export interface PersonnelSurveyAnswerInput {
  surveyQuestionId: string;

  /**
   * Required, and nullable so that omitting it is REFUSED rather than recorded.
   *
   * False is not a neutral value here — on item 35a it is the claim that the employee has never
   * been found guilty of an administrative offence.
   */
  answer: boolean | null;

  details?: string | null;
  dateFiled?: string | null;
  caseStatus?: string | null;
  idNumber?: string | null;
  countryId?: string | null;
}

/** What the modal sends. */
export interface PersonnelEligibilityInput {
  eligibilityId: string;
  rating?: number | null;
  dateOfExamination: string;
  placeOfExamination?: string | null;
  licenseNumber?: string | null;
  licenseValidUntil?: string | null;
}

/**
 * A line of CS Form 212 section III, in the order the form lists them.
 *
 * The numbers are the order: a 201 file reads its education from the earliest level up, so
 * the panel sorts on this rather than on a column somebody would have to keep in step.
 */
export type EducationLevel = 1 | 2 | 3 | 4 | 5;

export const EDUCATION_LEVEL_LABELS: Record<EducationLevel, string> = {
  1: 'Elementary',
  2: 'Secondary',
  3: 'Vocational / trade course',
  4: 'College',
  5: 'Graduate studies',
};

/** One line of educational background. */
export interface PersonnelEducation {
  id: string;
  level: EducationLevel;
  schoolName: string;
  degreeOrCourse?: string | null;

  /** Calendar dates, not instants — `formatDateOnly`, never `formatDate`. */
  periodFrom: string;
  periodTo?: string | null;

  highestLevelUnitsEarned?: string | null;

  /** A year, not a date: it is what a diploma states and what the form collects. */
  yearGraduated?: number | null;

  honors?: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends to record or correct one line. */
export interface PersonnelEducationInput {
  level: EducationLevel;
  schoolName: string;
  degreeOrCourse?: string | null;
  periodFrom: string;
  periodTo?: string | null;
  highestLevelUnitsEarned?: string | null;
  yearGraduated?: number | null;
  honors?: string | null;
}

export type AddressKind = 1 | 2;

export const ADDRESS_KIND = { present: 1, permanent: 2 } as const;

/** One row of a vocabulary the form picks from. */
export interface PersonnelCatalogEntry {
  id: string;
  name: string;
  sortOrder: number;
}

/** Every vocabulary the form needs, fetched in one call. */
export interface PersonnelCatalogs {
  bloodTypes: PersonnelCatalogEntry[];
  nationalities: PersonnelCatalogEntry[];
  religions: PersonnelCatalogEntry[];
  hmoProviders: PersonnelCatalogEntry[];

  /**
   * Why a personnel movement happened — original appointment, promotion, transfer,
   * reassignment.
   *
   * NOT `EmploymentType`, which is the STATUS of the appointment and is an enum the CSC
   * fixes. One person can be permanently appointed by promotion, and both facts are on the
   * form; a picker must not offer one where the other is asked for.
   */
  appointmentNatures: PersonnelCatalogEntry[];

  /**
   * Countries — for the dual-citizenship question, NOT the nationalities above.
   *
   * A nationality is a demonym ("Filipino"); the PDS asks for a country ("Philippines").
   * One is not an answer to the other, which is why they are two lists.
   */
  countries: PersonnelCatalogEntry[];

  /**
   * How a special skill or hobby is classified — NOT the competency framework.
   *
   * `Competency.category` groups the framework this organization measures L&D against, written
   * deliberately and revised on its own cadence. This groups an employee's own skills and
   * pastimes, and one employee can be an Expert photographer with no framework entry anywhere
   * near it.
   */
  specialSkillCategories: PersonnelCatalogEntry[];
}

export interface PersonnelAddressInput {
  kind: AddressKind;
  streetAddress: string | null;
  barangay: string | null;
  cityMunicipality: string | null;
  province: string | null;
  zipCode: string | null;
}

export interface PersonnelAddress extends PersonnelAddressInput {
  id: string;
}

/** The photograph, on its way to the identity store. */
export interface PersonPhotoInput {
  contentType: string;
  /** Base64, without the data-URL prefix — `byte[]` on the wire. */
  bytes: string;
}

/**
 * One vacant post, flat across the whole staffing pattern.
 *
 * The merged picker exists because a reader hiring someone thinks in one step — which post
 * are they going into — and answering that through a position picker and then an item picker
 * made them hold the plantilla in their head.
 */
export interface PlantillaVacantItem {
  id: string;
  plantillaPositionId: string;
  itemNumber: string;
  positionTitle: string;
  orgUnitName: string;
  salaryGrade: number | null;
}

/** How the Account step is answered. Three shapes, and the request expresses all three. */
export type AccountMode = 'skip' | 'link' | 'create';

/** An account to create alongside the record. No password: the server issues one. */
export interface PersonnelAccountInput {
  email: string;

  /** Locked to the employee's own name — this account belongs to the person the record is about. */
  alias: string;

  /*
    No roleId. An account created here starts with none: the person filling in an employment
    record is not the person who decides what that employee may do, so it appears in User
    management awaiting a role and reaches nothing until one is assigned.
  */
}

/** An account with no employee attached. */
export interface LinkableAccount {
  id: string;
  alias: string;
  email: string;
}

/** Shown once, never readable again. */
export interface UserCredentialIssued {
  userId: string;
  email: string;
  generatedPassword: string | null;
  mustChangePassword: boolean;
}

/** What a create returns: the record, and the credential when one was issued. */
export interface PersonnelCreated {
  personnel: PersonnelDetail;
  account: UserCredentialIssued | null;
}

/** One identification number, on its way in. A blank one is dropped by the server. */
export interface PersonnelIdentificationInput {
  identificationTypeId: string;

  /**
   * THREE STATES, and the middle one is what stops an edit destroying data.
   *
   * - `null` — not supplied, so whatever is recorded is **left alone**. This is what a form
   *   sends for a card whose number it could not reveal, so editing the place and the dates
   *   cannot wipe a number the reader was never shown.
   * - `''` — supplied and cleared. The number is removed.
   * - a value — recorded, replacing whatever was there.
   */
  number: string | null;

  placeIssued?: string | null;
  dateIssued?: string | null;
  validUntil?: string | null;
}

/** One identification number as stored. The type name is a live view of the catalog. */
export interface PersonnelIdentificationValue {
  id: string;
  identificationTypeId: string;
  typeName: string;
  typeCode: string | null;

  /*
    THERE IS NO NUMBER HERE, and its absence is the design.

    It lives in `rsys_pii` from 17 Sep 2026 and reading one is a REVEAL — one card at a time,
    audited, through `personnelApi.revealIdentificationNumber`. A number on this type would
    make every profile read a bulk disclosure of every card the employee holds.
  */

  /** Where the card was issued. */
  placeIssued?: string | null;

  /** A CALENDAR date — render it with `formatDateOnly`. */
  dateIssued?: string | null;

  /** Null for a card that does not expire, which is most of them. */
  validUntil?: string | null;
}

/**
 * Every entity CLR name a personnel write lands on — `Personnel` and all thirteen of its
 * child tables, plus one legacy name (below).
 *
 * `<HistoryLogsModal>` filters on `entityType`, so a list naming only `Personnel` hides every
 * section of the 201 file: recording a survey answer writes a `PersonnelSurveyAnswer` row, and
 * the module's history simply did not ask for it. **A section added to the 201 file adds its
 * entity here in the same change**, or its writes are audited and unreadable.
 *
 * `PersonnelAssignment` is the pre-rename name of `PersonnelServiceRecord` and is deliberately
 * kept: the audit trail is append-only, so rows written before `RenameAssignmentsToServiceRecords`
 * still carry the old CLR name. Dropping it would retire four real events from the history. **A
 * rename orphans its own trail unless the old name stays on this list.**
 *
 * `PersonnelIdentity` is the one class in that domain folder deliberately absent: it is not
 * mapped in `AppDbContext` at all — it lives in `PiiDbContext` over `rsys_pii` — so no write
 * to it is ever audited under that name. The identity store's own writes appear as
 * `PersonIdentityDto` / `PersonPhotoDto` and belong to the persons module, not this one.
 */
export const PERSONNEL_AUDIT_ENTITY_TYPES = [
  'Personnel',
  'PersonnelAddress',
  'PersonnelEducation',
  'PersonnelEligibility',
  'PersonnelEmergencyContact',
  'PersonnelIdentification',
  'PersonnelServiceRecord',
  'PersonnelAssignment',
  'PersonnelRecognition',
  'PersonnelReference',
  'PersonnelDisciplinaryRecord',
  'PersonnelSaln',
  'SalnRealProperty',
  'SalnPersonalProperty',
  'SalnLiability',
  'SalnBusinessInterest',
  'PersonnelSpecialSkill',
  'PersonnelSurveyAnswer',
  'PersonnelTraining',
  'PersonnelTrainingCompetency',
  'PersonnelVoluntaryWork',
  'PersonnelWorkExperience',
  'PersonnelFamilyMember',
];

/**
 * Classifies a personnel record, or clears its classification.
 *
 * Its own request and its own endpoint under `personnel.set-access-level`. Reclassifying
 * decides who can read the file at all, which is a decision about disclosure rather than a
 * correction to the record — folding it into the update would hand it to everybody who fixes a
 * contact number.
 */
export interface SetPersonnelAccessLevelRequest {
  /** The rung, or null to return the record to unclassified. */
  accessLevelId: string | null;
}
